import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseKey);

const BUCKET = 'property-photos';

// Two different limits — conflating them was a bug.
//
//   MAX_SOURCE_SIZE  what we accept from the picker. Only a sanity bound
//                    against someone selecting a video or a RAW file.
//                    A 48MP phone photo is ~15-25MB and compresses down
//                    to a few hundred KB, so rejecting it up front (the
//                    old behaviour: a flat 5MB gate BEFORE compression)
//                    meant better cameras failed more often.
//   MAX_UPLOAD_SIZE  what we actually store, checked AFTER compression.
//                    In practice never hit — a 1200px JPEG is ~200-400KB.
const MAX_SOURCE_SIZE = 40 * 1024 * 1024;
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024;

// Default compression preset — tuned for property listing photos where
// visual fidelity matters (marketing surface). Callers that store
// non-marketing photos (e.g. lease condition photos) can pass an
// override to uploadPhoto() to get tighter compression and save storage.
const DEFAULT_MAX_WIDTH = 1200;
const DEFAULT_QUALITY = 0.8;

// Compress image before upload. maxWidth scales down only — never upscales —
// so passing maxWidth=1024 on a 600px-wide image is a no-op.
//
// Hardened against three real-world failure modes we kept hitting:
//
//   1. iPhone HEIC photos picked through <input accept="image/*">.
//      iOS reports them as image/jpeg in some cases, but Chrome /
//      Firefox / Edge can't decode HEIC. <img>.onload never fires
//      → Promise hung forever → user stuck on "Uploading…".
//
//   2. Corrupted JPEGs / 0-byte files. Same hang.
//
//   3. canvas.toBlob() returning null (rare — happens when the
//      canvas pixel buffer is tainted, e.g. cross-origin source).
//      We resolved(null) and Supabase then 400'd with a cryptic
//      message.
//
// Now: reject promptly on any of those with a human-readable
// error. Add a 15s safety timeout so even unknown failure modes
// can't leave the user stuck.
const compressImage = (file, maxWidth, quality) => new Promise((resolve, reject) => {
  const img = new Image();
  const canvas = document.createElement('canvas');

  // Object URL instead of FileReader+data URL: a 20MB photo becomes a
  // ~27MB base64 string through FileReader, which is slow and memory
  // hungry on phones. createObjectURL hands the decoder the raw bytes.
  const url = URL.createObjectURL(file);
  const cleanup = () => { try { URL.revokeObjectURL(url); } catch (_) {} };

  // Safety timeout scaled to file size — a 25MB photo on a budget phone
  // legitimately needs longer to decode than a 2MB one.
  const budget = Math.min(45000, 15000 + Math.round((file.size || 0) / (1024 * 1024)) * 1500);
  const timer = setTimeout(() => {
    cleanup();
    reject(new Error("Couldn't read this image. Try a JPG or PNG."));
  }, budget);

  const fail = (msg) => {
    clearTimeout(timer);
    cleanup();
    reject(new Error(msg));
  };

  img.onerror = () => fail(
    "This image format isn't supported. iPhone users: switch your "
    + "Camera Capture setting to 'Most Compatible', or pick a JPG/PNG."
  );
  img.onload = () => {
    try {
      let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
      if (!w || !h) return fail("Image has no dimensions.");
      if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        clearTimeout(timer);
        cleanup();
        if (!blob) return reject(new Error("Couldn't compress this image."));
        resolve(blob);
      }, 'image/jpeg', quality);
    } catch (e) {
      fail(e?.message || "Image processing failed.");
    }
  };
  img.src = url;
});

// Compress, then step quality/width down until the result fits the
// storage cap. Guarded to 4 attempts so a pathological image can't loop.
const compressToFit = async (file, maxWidth, quality) => {
  let w = maxWidth, q = quality;
  let blob = await compressImage(file, w, q);
  for (let i = 0; i < 4 && blob.size > MAX_UPLOAD_SIZE; i++) {
    q = Math.max(0.5, q - 0.12);
    w = Math.round(w * 0.85);
    blob = await compressImage(file, w, q);
  }
  if (blob.size > MAX_UPLOAD_SIZE) {
    throw new Error("This image is too large to upload even after compression.");
  }
  return blob;
};

/**
 * Upload a photo to Supabase Storage.
 *
 * @param {File} file - the user-picked file
 * @param {string} propertyId - storage path prefix (works for any
 *        entity, not just properties — pass e.g. `lease_${id}` for
 *        lease photos)
 * @param {object} [options]
 * @param {number} [options.maxWidth=1200] - resize cap in px
 * @param {number} [options.quality=0.8]   - JPEG quality 0-1
 *
 * Existing callers (property listings) get the historical 1200/0.8 by
 * default; lease condition photos pass {maxWidth:1024, quality:0.75}
 * for ~25% storage savings with no visible quality drop for
 * documentation-grade photos.
 */
export const uploadPhoto = async (file, propertyId, options = {}) => {
  if (!file) throw new Error('No file selected');
  if (!file.size) throw new Error('This file is empty.');
  if (file.size > MAX_SOURCE_SIZE) {
    throw new Error('That file is too big to process. Pick a photo, not a video or RAW file.');
  }
  // iPhone HEIC/HEIF guard. The native <input accept="image/*"> picker
  // happily returns HEIC files on iOS, but browser <img> tags can't
  // decode them — so compressImage's onload never fires and the
  // upload hangs. Caught here with a useful message instead.
  const nameLower = (file.name || '').toLowerCase();
  const typeLower = (file.type || '').toLowerCase();
  const isHeic = nameLower.endsWith('.heic') || nameLower.endsWith('.heif')
    || typeLower.includes('heic') || typeLower.includes('heif');
  if (isHeic) {
    throw new Error(
      "HEIC photos aren't supported here. On iPhone: Settings → "
      + "Camera → Formats → 'Most Compatible'. Then re-take or pick "
      + "the photo again."
    );
  }

  const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH;
  const quality = options.quality != null ? options.quality : DEFAULT_QUALITY;

  const compressed = await compressToFit(file, maxWidth, quality);
  const ext = 'jpg';
  const fileName = `${propertyId}/${Date.now()}_${Math.random().toString(36).substr(2, 6)}.${ext}`;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(fileName, compressed, {
      contentType: 'image/jpeg',
      upsert: false
    });

  if (error) throw error;

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(data.path);
  return urlData.publicUrl;
};

// Delete photo from Supabase Storage.
//
// IMPORTANT: this can NOT be done from the browser. Supabase Storage's
// default RLS blocks DELETE for the anon key and (worse) returns
// "Object not found" rather than a permissions error — which let our
// UI silently report success while the blob lingered forever.
//
// We route every delete through our backend, which holds the service
// role key (server-side only, never exposed to clients) and runs an
// ownership check on the path prefix before deleting. See
// server/routes/storage.js and server/utils/supabaseStorage.js.
export const deletePhoto = async (url) => {
  try {
    if (!url) return { success: false, error: 'no_url' };
    // Resolve the API base the same way src/api.js does so this util
    // works in dev, production, and Capacitor APK — without forcing a
    // circular import from api.js into the upload pipeline.
    const PROD_API = process.env.REACT_APP_API_URL || 'https://api.prorentz.com/api';
    const DEV_API  = 'http://localhost:5000/api';
    const isCapacitor = typeof window !== 'undefined' && window.Capacitor
        && typeof window.Capacitor.isNativePlatform === 'function'
        && window.Capacitor.isNativePlatform();
    const isDevBrowser = typeof window !== 'undefined'
        && window.location.protocol === 'http:'
        && window.location.hostname === 'localhost';
    const apiBase = isCapacitor ? PROD_API : (isDevBrowser ? DEV_API : PROD_API);
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('auth_token')) || '';
    const res = await fetch(`${apiBase}/storage/delete-photo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ url }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      console.error('Delete photo failed:', res.status, data);
      return { success: false, error: data.error || `status_${res.status}` };
    }
    return { success: true, ...data };
  } catch (e) {
    console.error('Delete photo error:', e);
    return { success: false, error: e?.message || 'network_error' };
  }
};

export default supabase;
